/**
 * Utility functions for handling recurring tasks
 * 
 * Standard RecurrenceSettings format:
 * type RecurrencePattern = 'none' | 'daily' | 'weekly' | 'monthly';
 * export type Weekday = 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday' | 'sunday';
 * 
 * export interface RecurrenceSettings {
 *   pattern: RecurrencePattern;
 *   weekdays?: Weekday[]; // For weekly recurrence
 *   daysPerMonth?: number; // For monthly recurrence
 * }
 */

/**
 * Check if a task should recur on a given date
 * @param {Object} recurrence - The recurrence configuration
 * @param {Date} checkDate - The date to check
 * @param {Date} startDate - The task start date
 * @returns {boolean} - Whether the task should occur on the given date
 */
export const shouldTaskRecurOnDate = (recurrence, checkDate, startDate) => {
  if (!recurrence || !checkDate || !startDate) return false;

  const check = new Date(checkDate);
  const start = new Date(startDate);

  // Check if the date is before the start date
  if (check < start) return false;

  // Use consistent pattern format
  const pattern = recurrence.pattern;

  switch (pattern) {
    case 'daily':
      return shouldRecurDaily(recurrence, check, start);
    case 'weekly':
      return shouldRecurWeekly(recurrence, check, start);
    case 'monthly':
      return shouldRecurMonthly(recurrence, check, start);
    default:
      return false;
  }
};

/**
 * Check if a daily recurring task should occur on a given date
 */
const shouldRecurDaily = (recurrence, checkDate, startDate) => {
  const daysDiff = Math.floor((checkDate - startDate) / (1000 * 60 * 60 * 24));
  // Daily recurrence happens every day
  return daysDiff >= 0;
};

/**
 * Check if a weekly recurring task should occur on a given date
 */
const shouldRecurWeekly = (recurrence, checkDate, startDate) => {
  const daysDiff = Math.floor((checkDate - startDate) / (1000 * 60 * 60 * 24));
  const weeksDiff = Math.floor(daysDiff / 7);

  // Weekly recurrence happens every week
  if (weeksDiff < 0) return false;

  // Check if it's the right day of the week
  const dayOfWeek = checkDate.getDay(); // 0 = Sunday, 1 = Monday, etc.

  // Use consistent weekdays format (strings like 'monday', 'tuesday')
  const weekdays = recurrence.weekdays || [];

  if (weekdays.length > 0) {
    const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const currentDayName = dayNames[dayOfWeek];
    return weekdays.includes(currentDayName);
  }

  // If no specific days are set, default to the same day of week as start date
  const startDayOfWeek = startDate.getDay();
  return dayOfWeek === startDayOfWeek;
};

/**
 * Check if a monthly recurring task should occur on a given date
 */
const shouldRecurMonthly = (recurrence, checkDate, startDate) => {
  // Since database uses date type, use local date methods
  const monthsDiff = (checkDate.getFullYear() - startDate.getFullYear()) * 12 +
    (checkDate.getMonth() - startDate.getMonth());

  // Monthly recurrence happens every month
  if (monthsDiff < 0) return false;

  // Check if it's the right day of the month
  const dayOfMonth = checkDate.getDate();
  const expectedDayOfMonth = recurrence.daysPerMonth || startDate.getDate();
  return dayOfMonth === expectedDayOfMonth;
};

/**
 * Get the next occurrence date for a recurring task
 * @param {Object} recurrence - The recurrence configuration
 * @param {Date} fromDate - The date to start looking from
 * @param {Date} startDate - The task start date
 * @returns {Date|null} - The next occurrence date or null if no more occurrences
 */
export const getNextOccurrence = (recurrence, fromDate, startDate) => {
  if (!recurrence || !fromDate || !startDate) return null;

  const from = new Date(fromDate);
  const start = new Date(startDate);

  // If fromDate is before startDate, return startDate
  if (from < start) return start;

  // Use consistent pattern format
  const pattern = recurrence.pattern;

  switch (pattern) {
    case 'daily':
      return getNextDailyOccurrence(recurrence, from, start);
    case 'weekly':
      return getNextWeeklyOccurrence(recurrence, from, start);
    case 'monthly':
      return getNextMonthlyOccurrence(recurrence, from, start);
    default:
      return null;
  }
};

/**
 * Get the next daily occurrence
 */
const getNextDailyOccurrence = (recurrence, fromDate, _startDate) => {
  // Daily recurrence happens every day, so next occurrence is tomorrow
  const nextDate = new Date(fromDate);
  nextDate.setDate(fromDate.getDate() + 1);
  return nextDate;
};

/**
 * Get the next weekly occurrence
 */
const getNextWeeklyOccurrence = (recurrence, fromDate, startDate) => {
  const daysDiff = Math.floor((fromDate - startDate) / (1000 * 60 * 60 * 24));
  const weeksDiff = Math.floor(daysDiff / 7);

  // Weekly recurrence happens every week
  let nextWeekDiff = weeksDiff + 1;

  // Use consistent weekdays format (strings like 'monday', 'tuesday')
  const weekdays = recurrence.weekdays || [];

  let sortedDays = [];
  if (weekdays.length > 0) {
    // Convert weekdays strings to day numbers
    const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    sortedDays = weekdays
      .map(day => dayNames.indexOf(day.toLowerCase()))
      .filter(day => day !== -1)
      .sort();
  }

  // If no specific days are set, default to the same day of week as start date
  if (sortedDays.length === 0) {
    const startDayOfWeek = startDate.getDay();
    sortedDays = [startDayOfWeek];
  }

  // Find the next occurrence within that week
  const currentDayOfWeek = fromDate.getDay();

  // Find the next day in the current week
  const nextDayInWeek = sortedDays.find(day => day > currentDayOfWeek);

  if (nextDayInWeek !== undefined) {
    // Next occurrence is in the current week
    const nextDate = new Date(fromDate);
    nextDate.setDate(fromDate.getDate() + (nextDayInWeek - currentDayOfWeek));
    return nextDate;
  } else {
    // Next occurrence is in the next week
    const nextWeekStart = new Date(startDate);
    nextWeekStart.setDate(startDate.getDate() + (nextWeekDiff * 7));

    const nextDate = new Date(nextWeekStart);
    nextDate.setDate(nextWeekStart.getDate() + sortedDays[0]);
    return nextDate;
  }
};

/**
 * Get the next monthly occurrence
 */
const getNextMonthlyOccurrence = (recurrence, fromDate, startDate) => {
  // Since database uses date type, use local date methods
  const monthsDiff = (fromDate.getFullYear() - startDate.getFullYear()) * 12 +
    (fromDate.getMonth() - startDate.getMonth());

  // Monthly recurrence happens every month
  const nextMonthDiff = monthsDiff + 1;
  const nextDate = new Date(startDate);
  nextDate.setMonth(startDate.getMonth() + nextMonthDiff);
  nextDate.setDate(recurrence.daysPerMonth || startDate.getDate());

  return nextDate;
};

/**
 * Get a human-readable description of the recurrence pattern
 * @param {Object} recurrence - The recurrence configuration
 * @returns {string} - Human-readable description
 */
export const getRecurrenceDescription = (recurrence) => {
  if (!recurrence) return 'No recurrence';

  // Use consistent pattern format
  const pattern = recurrence.pattern;

  switch (pattern) {
    case 'daily':
      return 'Every day';
    case 'weekly':
      const weekdays = recurrence.weekdays || [];

      let selectedDays = [];
      if (weekdays.length > 0) {
        // Convert weekdays to readable format
        const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
        selectedDays = weekdays.map(day => dayNames.find(name => name.toLowerCase() === day.toLowerCase()) || day);
      }

      return `Every week on ${selectedDays.join(', ')}`;
    case 'monthly':
      const daysPerMonth = recurrence.daysPerMonth || 1;
      return ` ${daysPerMonth} dyas per month`;
    default:
      return 'No recurrence';
  }
};

/**
 * Get a short description of the recurrence pattern
 * @param {Object} recurrence - The recurrence configuration
 * @returns {string} - Short description
 */
export const getRecurrenceShortDescription = (recurrence) => {
  if (!recurrence) return null;

  // Use consistent pattern format
  const pattern = recurrence.pattern;

  switch (pattern) {
    case 'daily':
      return 'Daily';
    case 'weekly':
      const weekdays = recurrence.weekdays || [];

      let selectedDays = [];
      if (weekdays.length > 0) {
        // Convert weekdays to short format
        const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
        selectedDays = weekdays.map(day => dayNames.find(name => name.toLowerCase() === day.toLowerCase()) || day);
      }

      if (selectedDays.length > 0) {
        return `${selectedDays.join(', ')}`;
      } else {
        return 'Weekly';
      }
    case 'monthly':
      const daysPerMonth = recurrence.daysPerMonth || 1;
      return `${`${daysPerMonth}`} day${daysPerMonth > 1 ? 's' : ''} per month`;
    default:
      return null;
  }
};


/**
 * Check if a recurring task can be updated on the current date
 * @param {Object} task - The task object with recurrence and startDate
 * @param {Date} checkDate - The date to check (defaults to today)
 * @returns {boolean} - Whether the task can be updated on the given date
 */
export const canUpdateRecurringTask = (task, checkDate = new Date()) => {
  // If task has no recurrence, it can always be updated
  if (!task.recurrence || task.recurrence.pattern === 'none') {
    return true;
  }

  // If task has no start date, it can always be updated
  if (!task.startDate) {
    return true;
  }

  // Check if the task should recur on the given date
  return shouldTaskRecurOnDate(task.recurrence, checkDate, new Date(task.startDate));
};

/**
 * Get a user-friendly message explaining why a recurring task cannot be updated
 * @param {Object} task - The task object with recurrence and startDate
 * @param {Date} checkDate - The date to check (defaults to today)
 * @returns {string|null} - The message or null if task can be updated
 */
export const getRecurringTaskUpdateMessage = (task, checkDate = new Date()) => {
  if (canUpdateRecurringTask(task, checkDate)) {
    return null;
  }

  const nextOccurrence = getNextOccurrence(task.recurrence, checkDate, new Date(task.startDate));

  if (nextOccurrence) {
    const nextDateStr = nextOccurrence.toLocaleDateString();
    return `This recurring task can only be updated on its scheduled days. Next occurrence: ${nextDateStr}`;
  }

  return 'This recurring task can only be updated on its scheduled days.';
};

// Helper functions for date handling
// Since database uses date type (YYYY-MM-DD), dates are already simple date strings
const normalizeDate = (date) => {
  if (!date) return null;

  if (typeof date === 'string') {
    const trimmed = date.trim();
    const datePortion = trimmed.includes('T') ? trimmed.split('T')[0] : trimmed;
    if (/^\d{4}-\d{2}-\d{2}$/.test(datePortion)) {
      const [year, month, day] = datePortion.split('-').map(Number);
      return new Date(year, month - 1, day, 0, 0, 0, 0);
    }
    const normalized = new Date(trimmed);
    if (isNaN(normalized.getTime())) return null;
    normalized.setHours(0, 0, 0, 0);
    return normalized;
  }

  const normalized = new Date(date);
  if (isNaN(normalized.getTime())) return null;
  normalized.setHours(0, 0, 0, 0);
  return normalized;
};

const toSafeDateString = (value) => {
  const normalized = normalizeDate(value);
  return normalized ? normalized.toDateString() : null;
};

/**
 * Get all scheduled dates for a recurring task between start and end dates
 * @param {Object} recurrence - The recurrence configuration
 * @param {Date} startDate - The task start date
 * @param {Date} endDate - The task end date
 * @returns {Array<Date>} - Array of scheduled dates
 */
export const getScheduledDates = (recurrence, startDate, endDate) => {
  if (!recurrence || !startDate || !endDate) return [];

  const start = new Date(startDate);
  const end = new Date(endDate);

  // Use consistent pattern format
  const pattern = recurrence.pattern;

  switch (pattern) {
    case 'daily':
      return getDailyScheduledDates(recurrence, start, end);
    case 'weekly':
      return getWeeklyScheduledDates(recurrence, start, end);
    case 'monthly':
      return getMonthlyScheduledDates(recurrence, start, end);
    default:
      return [];
  }
};

/**
 * Get scheduled dates for daily recurring tasks
 */
const getDailyScheduledDates = (recurrence, startDate, endDate) => {
  const scheduledDates = [];
  const current = new Date(startDate);

  while (current <= endDate) {
    scheduledDates.push(new Date(current));
    current.setUTCDate(current.getUTCDate() + 1); // Daily means every day
  }

  return scheduledDates;
};

/**
 * Get scheduled dates for weekly recurring tasks
 */
const getWeeklyScheduledDates = (recurrence, startDate, endDate) => {
  const scheduledDates = [];

  // Use consistent weekdays format (strings like 'monday', 'tuesday')
  const weekdays = recurrence.weekdays || [];

  let selectedDays = [];
  if (weekdays.length > 0) {
    // Convert weekdays strings to day numbers
    const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    selectedDays = weekdays
      .map(day => dayNames.indexOf(day.toLowerCase()))
      .filter(day => day !== -1);
  }

  // If no specific days are set, default to the same day of week as start date
  if (selectedDays.length === 0) {
    selectedDays = [startDate.getDay()];
  }

  const current = new Date(startDate);
  const end = new Date(endDate);

  // Start from the beginning of the week containing the start date
  const startOfWeek = new Date(current);
  startOfWeek.setDate(current.getDate() - current.getDay());

  let weekStart = new Date(startOfWeek);

  while (weekStart <= end) {
    // Check each selected day of the week
    for (const dayOfWeek of selectedDays) {
      const scheduledDate = new Date(weekStart);
      scheduledDate.setDate(weekStart.getDate() + dayOfWeek);

      if (scheduledDate >= startDate && scheduledDate <= endDate) {
        scheduledDates.push(new Date(scheduledDate));
      }
    }

    // Move to next week
    weekStart.setDate(weekStart.getDate() + 7);
  }

  return scheduledDates.sort((a, b) => a - b);
};

/**
 * Get scheduled dates for monthly recurring tasks
 * daysPerMonth = number of days the task needs to be done in a month
 */
const getMonthlyScheduledDates = (_recurrence, _startDate, _endDate) => {
  // For monthly tasks, we don't need to generate specific scheduled dates
  // since the progress is calculated based on actual completed entries
  // Return empty array - the totalRequired will be calculated in calculateRecurringTaskProgress
  return [];
};

/**
 * Calculate progress for a recurring task based on progressHistory entries marked as completed
 * @param {Object} task - The task object with recurrence, startDate, endDate, and progressHistory
 * @returns {Object} - Object with calculated progress and completion stats
 */
export const calculateRecurringTaskProgress = (task, options = {}) => {
  const { isTillToday = false } = options;
  const startDate = normalizeDate(task?.startDate);

  // Fallback to "today" when endDate is missing
  const today = normalizeDate(new Date());
  let endDate;

  if (isTillToday) {
    const rawEndDate = task?.endDate ? normalizeDate(task.endDate) : today;
    // Cap at today
    endDate = today < rawEndDate ? today : rawEndDate;
  } else {
    const rawEndDate = task?.endDate ? task.endDate : new Date();
    endDate = normalizeDate(rawEndDate);
  }

  if (!startDate) {
    return {
      progress: 0,
      totalRequired: 0,
      completedEntries: 0,
      completionRate: '0/0'
    };
  }

  // Use recurrence pattern, default to 'none' (non-recurring)
  const recurrence = task.recurrence || { pattern: 'none' };
  const pattern = recurrence.pattern;

  // For totalRequired
  let totalRequired;
  let scheduledDates = [];

  if (pattern === 'monthly') {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const startYear = start.getFullYear();
    const startMonth = start.getMonth();
    const endYear = end.getFullYear();
    const endMonth = end.getMonth();
    const totalMonths = (endYear - startYear) * 12 + (endMonth - startMonth) + 1;
    totalRequired = totalMonths * (recurrence.daysPerMonth || 1);
  } else {
    // For non-recurring (pattern === 'none') or daily, treat it as daily for the scheduled dates range
    const effectiveRecurrence = (pattern === 'none' || pattern === 'daily')
      ? { pattern: 'daily' }
      : recurrence;

    scheduledDates = getScheduledDates(effectiveRecurrence, startDate, endDate);
    totalRequired = scheduledDates.length;
  }

  if (totalRequired === 0) {
    return {
      progress: 0,
      totalRequired: 0,
      completedEntries: 0,
      completionRate: '0/0'
    };
  }

  // Count completed entries from progressHistory where markedAsCompleted is true
  let completedEntries = 0;
  if (task.progressHistory && Array.isArray(task.progressHistory)) {
    // Count unique dates that have been marked as completed
    const completedDates = new Set();

    task.progressHistory.forEach(entry => {
      if (!entry?.markedAsCompleted) return;

      const entryDate = normalizeDate(entry.progressDate || entry.progressDateTime);
      if (!entryDate) {
        return;
      }

      if (pattern === 'monthly') {
        if (entryDate.getTime() >= startDate.getTime() && entryDate.getTime() <= endDate.getTime()) {
          completedDates.add(entryDate.toDateString());
        }
        return;
      }

      // If scheduledDates is empty (shouldn't happen for daily/weekly), we fallback
      const isScheduledDate = scheduledDates.length === 0 || scheduledDates.some(scheduledDate =>
        toSafeDateString(entryDate) === toSafeDateString(scheduledDate)
      );

      if (isScheduledDate) {
        completedDates.add(entryDate.toDateString());
      }
    });

    completedEntries = completedDates.size;

    // Return 0 if no completed entries
    if (completedEntries <= 0) {
      return { progress: 0, totalRequired: totalRequired, completedEntries: 0, completionRate: `0/${totalRequired}` };
    }
  }
  const progress = totalRequired > 0 ? (completedEntries / totalRequired) * 100 : 0;

  return {
    progress: Math.round(progress), // Round to nearest integer
    totalRequired,
    completedEntries,
    completionRate: `${completedEntries}/${totalRequired}`
  };
};

/**
 * Check if a specific date is completed for a recurring task
 * @param {Object} task - The task object with progressHistory
 * @param {Date} checkDate - The date to check
 * @returns {boolean} - Whether the date is marked as completed
 */
export const isDateCompleted = (task, checkDate) => {
  if (!task.progressHistory || !Array.isArray(task.progressHistory)) {
    return false;
  }

  const normalizedCheck = normalizeDate(checkDate);
  if (!normalizedCheck) return false;
  const checkDateStr = normalizedCheck.toDateString();

  return task.progressHistory.some(entry => {
    if (entry?.markedAsCompleted) {
      const entryDate = normalizeDate(entry.progressDate || entry.progressDateTime);
      if (!entryDate) return false;
      return entryDate.toDateString() === checkDateStr;
    }
    return false;
  });
};

/**
 * Get the latest progress entry for a specific date
 * @param {Object} task - The task object with progressHistory
 * @param {Date} checkDate - The date to check
 * @returns {Object|null} - The latest progress entry or null
 */
export const getLatestProgressForDate = (task, checkDate) => {
  if (!task.progressHistory || !Array.isArray(task.progressHistory)) {
    return null;
  }

  const normalizedCheck = normalizeDate(checkDate);
  if (!normalizedCheck) return null;
  const checkDateStr = normalizedCheck.toDateString();
  const dateEntries = task.progressHistory.filter(entry => {
    if (entry?.progressDate || entry?.progressDateTime) {
      const entryDate = normalizeDate(entry.progressDate || entry.progressDateTime);
      if (!entryDate) return false;
      return entryDate.toDateString() === checkDateStr;
    }
    return false;
  });

  if (dateEntries.length === 0) return null;

  // Return the latest entry for this date
  return dateEntries.sort((a, b) => new Date(b.updatedOn) - new Date(a.updatedOn))[0];
}; 